import { formatMoney } from '../../components/data-grid/money';
import { sheetOf } from '../../components/report-grid/export';
import { ReportGrid } from '../../components/report-grid/ReportGrid';
import type { ReportGridColumn } from '../../components/report-grid/ReportGrid';
import { useI18n } from '../../i18n/I18nProvider';
import type { Language } from '../../i18n/catalogs';
import type { ChessTableReport, ChessTableRow } from './reports.api';
import { ReportFrame } from './ReportFrame';
import { reportSheetName } from './totals';

export interface ChessTableViewProps {
  report: ChessTableReport;
  language: Language;
  periodLabel: string;
}

interface ChessRowView {
  row: ChessTableRow;
  cells: Array<{ code: string; amount: string | null }>;
}

const EMPTY = '—';

export function ChessTableView({ report, language, periodLabel }: ChessTableViewProps): JSX.Element {
  const { t } = useI18n();

  const cellOf = (row: ChessTableRow, code: string): string | null =>
    row.cells.find((cell) => cell.accountCode === code)?.amount ?? null;

  const rows: ChessRowView[] = report.rows.map((row) => ({
    row,
    cells: report.columns.map((column) => ({ code: column.accountCode, amount: cellOf(row, column.accountCode) })),
  }));

  const columns: Array<ReportGridColumn<ChessRowView>> = [
    {
      key: 'account',
      header: t('reportsDebitCredit'),
      render: (view) => <span className="report__group-name">{view.row.accountCode}</span>,
      exportValue: (view) => view.row.accountCode,
    },
    ...report.columns.map((column) => ({
      key: `col-${column.accountCode}`,
      header: column.accountCode,
      align: 'right' as const,
      numeric: true,
      render: (view: ChessRowView) => {
        const amount = view.cells.find((cell) => cell.code === column.accountCode)?.amount ?? null;
        return amount === null ? EMPTY : formatMoney(amount, language);
      },
      exportValue: (view: ChessRowView) =>
        view.cells.find((cell) => cell.code === column.accountCode)?.amount ?? '',
    })),
    {
      key: 'total',
      header: t('reportsRowTotal'),
      align: 'right',
      numeric: true,
      render: (view) => formatMoney(view.row.total, language),
      exportValue: (view) => view.row.total,
    },
  ];

  const sheet = sheetOf(
    reportSheetName('chess-table', report),
    columns.map((column) => ({
      header: column.header,
      numeric: column.numeric,
      exportValue: column.exportValue,
    })),
    rows,
    [
      t('reportsColumnTotal'),
      ...report.columns.map((column) => column.total),
      report.grandTotal,
    ],
  );

  return (
    <ReportFrame
      title={t('reportsChessTable')}
      subtitle={t('reportsChessCaption')}
      periodLabel={periodLabel}
      sheet={sheet}
    >
      <ReportGrid
        columns={columns}
        rows={rows}
        getRowKey={(view) => view.row.accountCode}
        totals={[
          t('reportsColumnTotal'),
          ...report.columns.map((column) => formatMoney(column.total, language)),
          formatMoney(report.grandTotal, language),
        ]}
        emptyText={t('reportsEmpty')}
        stickyFirstColumn
        testId="chess-table"
      />
      <p className="report__note" data-testid="chess-grand-total">
        {t('reportsGrandTotal')}: {formatMoney(report.grandTotal, language)}
      </p>
    </ReportFrame>
  );
}
